import React from 'react';
import { Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export const SettingsTab = ({ facilityItems, handleAddItem, handleItemChange, handleRemoveItem, handleSave, loading, saving, token }) => (
            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle>Facility Overview Template</CardTitle>
                  <CardDescription>
                    Configure the facility overview questions that appear on all tacticals.
                    When users create a new tactical or roll over to the next month, these questions will be used.
                    You can have up to 10 items.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading...
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {facilityItems.map((item, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-sm font-medium text-muted-foreground w-6">
                            {index + 1}.
                          </span>
                          <Input
                            value={item}
                            onChange={(e) => handleItemChange(index, e.target.value)}
                            placeholder={`Facility item ${index + 1}`}
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive hover:text-destructive"
                            disabled={facilityItems.filter(i => i.trim()).length <= 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      
                      {facilityItems.length < 10 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddItem}
                          className="mt-2"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Item
                        </Button>
                      )}
                      
                      <div className="pt-6 border-t mt-6">
                        <Button onClick={handleSave} disabled={saving}>
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? 'Saving...' : 'Save Template'}
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2">
                          Changes will apply to all new tacticals and rollovers.
                        </p>
                      </div>
  
                      {/* Repair Section */}
                      <div className="pt-6 border-t mt-6">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Repair Tool</h4>
                        <p className="text-xs text-muted-foreground mb-3">
                          If any users have empty Facility Overview sections after a rollover, this tool will populate
                          their facility sites with the current template items. Only affects tacticals with empty items.
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          data-testid="repair-facility-sites-btn"
                          onClick={async () => {
                            try {
                              const resp = await axios.post(
                                `${API_BASE}/admin/repair-empty-facility-sites?authorization=Bearer ${token}`
                              );
                              const data = resp.data;
                              if (data.repaired_count > 0) {
                                toast.success(`Repaired ${data.repaired_count} tactical(s): ${data.repaired_users.join(', ')}`);
                              } else {
                                toast.info('No tacticals need repair. All facility sites have items.');
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Repair failed');
                            }
                          }}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Repair Empty Facility Sites
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
);
